import axios from "axios";
import { toast } from "react-toastify";
import { getCookie } from "cookies-next";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { FaXmark, FaEye, FaTrash, FaPlus } from "react-icons/fa6";
import {
  GridComponent,
  Inject,
  ColumnsDirective,
  ColumnDirective,
  Search,
  Page,
  Sort,
  Toolbar,
  Resize,
  ExcelExport,
} from "@syncfusion/ej2-react-grids";
import { DropDownListComponent } from "@syncfusion/ej2-react-dropdowns";
import { Query } from "@syncfusion/ej2-data";

import "../../table.css";
import { API_ROOT } from "../../../config";
import { Header, PageLoading } from "../../../components";
import { useStateContext } from "../../../contexts/ContextProvider";
import { FaEdit, FaSync } from "react-icons/fa";
import React from "react";

const MembershipTypes = () => {
  const navigate = useNavigate();
  const { currentColor } = useStateContext();

  const [pageLoading, setPageLoading] = useState(true);
  const [membershipList, setMembershipList] = useState([]);
  const [refresh, setRefresh] = useState(0);
  const [toggleAdd, setToggleAdd] = useState(false);
  const [toggleEdit, setToggleEdit] = useState(false);
  const [toggleDetail, setToggleDetail] = useState(false);
  const [toggleDelete, setToggleDelete] = useState(false);
  const [toggleSync, setToggleSync] = useState(false);
  const [isDetailMode, setIsDetailMode] = useState(false);

  // Master data
  const [integratedPlatforms, setIntegratedPlatforms] = useState([]);

  const [membershipData, setMembershipData] = useState({
    uuid: null,
    integrated_platform_id: null,
    name: null,
    title_property: null,
    key_property: null,
    is_active: false,
  });

  const [membershipWarning, setMembershipWarning] = useState({
    integrated_platform_id: null,
    name: null,
    title_property: null,
    key_property: null,
    is_active: null,
  });

  const [syncMembershipData, setSyncMembershipData] = useState({
    integrated_platform_id: null,
  });

  const [syncMembershipWarning, setSyncMembershipWarning] = useState({
    integrated_platform_id: null,
  });

  const [membershipDisabled, setMembershipDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const gridRef = useRef(null);

  const formatDate = (inputDate) => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const date = new Date(inputDate);
    const dayOfWeek = days[date.getUTCDay()];
    const day = date.getUTCDate();
    const month = months[date.getUTCMonth()];
    const year = date.getUTCFullYear();
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const seconds = date.getUTCSeconds();

    return `${dayOfWeek}, ${day} ${month} ${year} ${hours
      .toString()
      .padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  const fetchIntegratedPlatforms = async () => {
    try {
      const response = await axios.get(
        API_ROOT + "/admin/user/integrated-platform/list?offset=1&limit=all",
        {
          headers: { Authorization: getCookie("admin_auth") },
        }
      );
      setIntegratedPlatforms(
        response.data.data.map((item) => ({
          text: item.platform_name,
          value: item.uuid,
        }))
      );
    } catch (error) {
      console.log("Failed to fetch integrated platforms:", error);
      if (error.response?.status === 401) {
        navigate("/login");
      }
    }
  };

  const fetchData = async () => {
    try {
      const response = await axios.get(
        API_ROOT + "/admin/membership/list?offset=1&limit=all",
        {
          headers: {
            Authorization: getCookie("admin_auth"),
          },
        }
      );

      const data = response.data.data;
      setMembershipList(() =>
        data.map((item, index) => ({
          No: index + 1,
          Uuid: item.uuid,
          IntegratedPlatformName:
            item.integrated_platform?.platform_name || "-",
          Name: item.name,
          TitleProperty: item.title_property,
          KeyProperty: item.key_property,
          IsActive: item.is_active,
          CreatedAt: formatDate(item.created_at),
        }))
      );
      setPageLoading(false);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      console.log(error);
      toast.error("Failed to get data");
      setPageLoading(false);
    }
  };

  const fetchMembershipDetail = async (uuid) => {
    try {
      const response = await axios.get(
        API_ROOT + "/admin/membership/detail/" + uuid,
        {
          headers: { Authorization: getCookie("admin_auth") },
        }
      );

      const data = response.data.data;
      setMembershipData({
        uuid: data.uuid,
        integrated_platform_id: data.integrated_platform.uuid,
        name: data.name,
        title_property: data.title_property,
        key_property: data.key_property,
        is_active: data.is_active,
      });
    } catch (error) {
      toast.error("Failed to fetch membership details");
      console.log(error);
    }
  };

  useEffect(() => {
    setPageLoading(true);
    setMembershipList([]);
    setToggleAdd(false);
    setToggleEdit(false);
    setToggleDetail(false);
    setToggleDelete(false);
    setToggleSync(false);
    setLoading(false);
    setMembershipDisabled(false);
    fetchData();
    fetchIntegratedPlatforms();
  }, [refresh]);

  useEffect(() => {
    if (!isDetailMode) {
      for (const value of Object.values(membershipWarning)) {
        if (value !== null) {
          setMembershipDisabled(true);
          return;
        }
      }
      setMembershipDisabled(false);
    }
  }, [membershipWarning, isDetailMode]);

  const actionTemplate = (props) => {
    return (
      <div className="e-actionParent flex gap-2">
        <button className="e-action" onClick={() => handleDetail(props)}>
          <FaEye className="e-actionIcon" />
          <div>Detail</div>
        </button>
        <button className="e-action" onClick={() => handleEdit(props)}>
          <FaEdit className="e-actionIcon" />
          <div>Edit</div>
        </button>
        {/* <button
          className="e-action text-red-600"
          onClick={() => handleDelete(props)}
        >
          <FaTrash className="e-actionIcon" />
          <div>Delete</div>
        </button> */}
      </div>
    );
  };

  const handleAdd = () => {
    setIsDetailMode(false);
    resetForm();
    setToggleAdd(true);
  };

  const handleDetail = async (data) => {
    setIsDetailMode(true);
    await fetchMembershipDetail(data.Uuid);
    setToggleDetail(true);
  };

  const handleEdit = async (data) => {
    setIsDetailMode(false);
    await fetchMembershipDetail(data.Uuid);
    setToggleEdit(true);
  };

  const handleSync = () => {
    resetSyncForm();
    setToggleSync(true);
  };

  const handleDelete = async (data) => {
    await fetchMembershipDetail(data.Uuid);
    setToggleDelete(true);
  };

  const queryCellInfo = (args) => {
    args.cell?.classList.add("e-cell");
  };

  function onIntegratedPlatformFiltering(args) {
    let query = new Query();
    query =
      args.text !== ""
        ? query.where("text", "contains", args.text, true)
        : query;
    args.updateData(integratedPlatforms, query);
  }

  const dataBound = () => {
    if (gridRef.current) {
      gridRef.current.autoFitColumns([
        "No",
        "IntegratedPlatformName",
        "Name",
        "TitleProperty",
        "KeyProperty",
        "CreatedAt",
        "Uuid",
      ]);
    }
  };

  const toolbarClick = (args) => {
    if (gridRef.current && args.item.id.includes("excelexport")) {
      const excelExportProperties = {
        fileName: "Paperless - Digitalisasi Untuk Negeri Membership List.xlsx",
      };
      gridRef.current.excelExport(excelExportProperties);
    }
  };

  const resetForm = () => {
    setMembershipData({
      uuid: null,
      integrated_platform_id: null,
      name: null,
      title_property: null,
      key_property: null,
      is_active: false,
    });
    setMembershipWarning({
      integrated_platform_id: null,
      name: null,
      title_property: null,
      key_property: null,
      is_active: null,
    });
  };

  const resetSyncForm = () => {
    setSyncMembershipData({
      integrated_platform_id: null,
    });
    setSyncMembershipWarning({
      integrated_platform_id: null,
    });
  };

  const validateForm = () => {
    let isValid = true;
    const warnings = { ...membershipWarning };

    if (!membershipData.integrated_platform_id) {
      warnings.integrated_platform_id = "Integrated Platform is required";
      isValid = false;
    } else {
      warnings.integrated_platform_id = null;
    }

    if (!membershipData.name || membershipData.name.trim().length < 1) {
      warnings.name = "Name is required";
      isValid = false;
    } else {
      warnings.name = null;
    }

    if (
      !membershipData.title_property ||
      membershipData.title_property.trim().length < 1
    ) {
      warnings.title_property = "Title Property is required";
      isValid = false;
    } else {
      warnings.title_property = null;
    }

    if (
      !membershipData.key_property ||
      membershipData.key_property.trim().length < 1
    ) {
      warnings.key_property = "Key Property is required";
      isValid = false;
    } else {
      warnings.key_property = null;
    }

    setMembershipWarning(warnings);
    return isValid;
  };

  const handleSubmit = async (isEdit = false) => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload = {
        integrated_platform_id: membershipData.integrated_platform_id,
        name: membershipData.name,
        title_property: membershipData.title_property,
        key_property: membershipData.key_property,
        is_active: membershipData.is_active,
      };

      if (isEdit) {
        await axios.put(
          API_ROOT + "/admin/membership/update/" + membershipData.uuid,
          payload,
          {
            headers: { Authorization: getCookie("admin_auth") },
          }
        );
        toast.success("Membership Updated!");
      } else {
        await axios.post(API_ROOT + "/admin/membership/create", payload, {
          headers: { Authorization: getCookie("admin_auth") },
        });
        toast.success("Membership Created!");
      }

      setToggleAdd(false);
      setToggleEdit(false);
      setRefresh((prev) => prev + 1);
    } catch (error) {
      toast.error(error.response?.data?.message || "Server Error");
      toast.error(
        isEdit
          ? "Failed to update membership type"
          : "Failed to create membership type"
      );

      if (error.response?.status === 401) {
        navigate("/login");
      }
    }

    setLoading(false);
  };

  const handleSyncSubmit = async () => {
    if (!syncMembershipData.integrated_platform_id) {
      setSyncMembershipWarning({
        integrated_platform_id: "Integrated Platform is required",
      });
      return;
    } else {
      setSyncMembershipWarning({
        integrated_platform_id: null,
      });
    }

    setLoading(true);

    try {
      const payload = {
        integrated_platform_id: syncMembershipData.integrated_platform_id,
      };

      await axios.post(API_ROOT + "/admin/membership/sync", payload, {
        headers: { Authorization: getCookie("admin_auth") },
      });
      toast.success("Membership Sync!");

      setToggleSync(false);
      setRefresh((prev) => prev + 1);
    } catch (error) {
      toast.error(error.response?.data?.message || "Server Error");

      if (error.response?.status === 401) {
        navigate("/login");
      }
    }

    setLoading(false);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);

    try {
      await axios.delete(
        API_ROOT + "/admin/membership/delete/" + membershipData.uuid,
        {
          headers: { Authorization: getCookie("admin_auth") },
        }
      );

      toast.success("Membership Deleted!");
      setToggleDelete(false);
      setRefresh((prev) => prev + 1);
    } catch (error) {
      toast.error("Failed to delete membership!");

      if (error.response?.status === 401) {
        navigate("/login");
      }
    }

    setDeleteLoading(false);
  };

  const renderFormFields = () => (
    <div className="flex flex-col gap-4 w-fit">
      <div className="flex flex-col gap-2">
        <h4 className="font-medium">Integrated Platform *</h4>
        <DropDownListComponent
          allowFiltering
          filtering={
            (onIntegratedPlatformFiltering =
              onIntegratedPlatformFiltering.bind(this))
          }
          dataSource={integratedPlatforms}
          fields={{ text: "text", value: "value" }}
          placeholder="Select integrated platform"
          value={membershipData.integrated_platform_id}
          change={(e) => {
            if (e.value) {
              setMembershipData((prev) => ({
                ...prev,
                integrated_platform_id: e.value,
              }));
              setMembershipWarning((prev) => ({
                ...prev,
                integrated_platform_id: null,
              }));
            } else {
              setMembershipWarning((prev) => ({
                ...prev,
                integrated_platform_id: "Integrated Platform Required",
              }));
            }
          }}
          enabled={!isDetailMode}
          cssClass={membershipWarning.integrated_platform_id ? "e-error" : ""}
          width="400px"
        />
        {membershipWarning.integrated_platform_id && (
          <p className="text-xs text-red-500">
            {membershipWarning.integrated_platform_id}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="font-medium">Name *</h4>
        <input
          type="text"
          placeholder="Input name..."
          maxLength={100}
          spellCheck="false"
          className={
            "w-[400px] p-2 text-sm rounded-[4px] border focus:outline-2 " +
            (!membershipWarning.name
              ? "border-slate-500 focus:outline-sky-500"
              : "border-red-500 focus:outline-red-500")
          }
          value={membershipData.name || ""}
          disabled={isDetailMode}
          onChange={(e) => {
            setMembershipData((prev) => ({ ...prev, name: e.target.value }));
            if (e.target.value.trim().length < 1) {
              setMembershipWarning((prev) => ({
                ...prev,
                name: "Name is required",
              }));
            } else {
              setMembershipWarning((prev) => ({ ...prev, name: null }));
            }
          }}
        />
        {membershipWarning.name && (
          <p className="text-xs text-red-500">{membershipWarning.name}</p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="font-medium">Title Property *</h4>
        <input
          type="text"
          placeholder="Input title property..."
          maxLength={100}
          spellCheck="false"
          className={
            "w-[400px] p-2 text-sm rounded-[4px] border focus:outline-2 " +
            (!membershipWarning.title_property
              ? "border-slate-500 focus:outline-sky-500"
              : "border-red-500 focus:outline-red-500")
          }
          value={membershipData.title_property || ""}
          disabled={isDetailMode}
          onChange={(e) => {
            setMembershipData((prev) => ({
              ...prev,
              title_property: e.target.value,
            }));
            if (e.target.value.trim().length < 1) {
              setMembershipWarning((prev) => ({
                ...prev,
                title_property: "Title Property is required",
              }));
            } else {
              setMembershipWarning((prev) => ({
                ...prev,
                title_property: null,
              }));
            }
          }}
        />
        {membershipWarning.title_property && (
          <p className="text-xs text-red-500">
            {membershipWarning.title_property}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="font-medium">Key Property *</h4>
        <input
          type="text"
          placeholder="Input key property..."
          maxLength={100}
          spellCheck="false"
          className={
            "w-[400px] p-2 text-sm rounded-[4px] border focus:outline-2 " +
            (!membershipWarning.key_property
              ? "border-slate-500 focus:outline-sky-500"
              : "border-red-500 focus:outline-red-500")
          }
          value={membershipData.key_property || ""}
          disabled={isDetailMode}
          pattern="[A-Za-z0-9]*"
          onChange={(e) => {
            const inputValue = e.target.value;
            const regex = /^[a-zA-Z0-9]*$/;

            if (regex.test(inputValue)) {
              setMembershipData((prev) => ({
                ...prev,
                key_property: inputValue,
              }));
            }
            if (inputValue.trim().length < 1) {
              setMembershipWarning((prev) => ({
                ...prev,
                key_property: "Key Property is required",
              }));
            } else {
              setMembershipWarning((prev) => ({ ...prev, key_property: null }));
            }
          }}
        />
        {membershipWarning.key_property && (
          <p className="text-xs text-red-500">
            {membershipWarning.key_property}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="font-medium">Is Active *</h4>
        <DropDownListComponent
          dataSource={[
            { text: "Active", value: true },
            { text: "Inactive", value: false },
          ]}
          fields={{ text: "text", value: "value" }}
          value={membershipData.is_active}
          change={(e) => {
            setMembershipData((prev) => ({
              ...prev,
              is_active: e.value,
            }));
            setMembershipWarning((prev) => ({
              ...prev,
              is_active: null,
            }));
          }}
          enabled={!isDetailMode}
          cssClass={membershipWarning.is_active ? "e-error" : ""}
          width="400px"
        />
        {membershipWarning.is_active && (
          <p className="text-xs text-red-500">{membershipWarning.is_active}</p>
        )}
      </div>
    </div>
  );

  const renderSyncFormFields = () => (
    <div className="flex flex-col gap-2">
      <h4 className="font-medium">Integrated Platform *</h4>
      <DropDownListComponent
        allowFiltering
        filtering={
          (onIntegratedPlatformFiltering =
            onIntegratedPlatformFiltering.bind(this))
        }
        dataSource={integratedPlatforms}
        fields={{ text: "text", value: "value" }}
        placeholder="Select integrated platform"
        value={syncMembershipData.integrated_platform_id}
        change={(e) => {
          if (e.value) {
            setSyncMembershipData({
              integrated_platform_id: e.value,
            });
            setSyncMembershipWarning({
              integrated_platform_id: null,
            });
          } else {
            setSyncMembershipWarning({
              integrated_platform_id: "Integrated Platform Required",
            });
          }
        }}
        enabled={!syncMembershipWarning.integrated_platform_id}
        cssClass={syncMembershipWarning.integrated_platform_id ? "e-error" : ""}
        width="400px"
      />
      {syncMembershipWarning.integrated_platform_id && (
        <p className="text-xs text-red-500">
          {syncMembershipWarning.integrated_platform_id}
        </p>
      )}
    </div>
  );

  return pageLoading ? (
    <PageLoading />
  ) : (
    <div className="m-2 md:m-10 mt-24 px-2 py-10 md:p-10 bg-white rounded-3xl">
      <div className="flex justify-between items-end pb-4">
        <Header title="Membership Data" />
        <div className="flex flex-wrap gap-2">
          <button
            style={{ backgroundColor: currentColor }}
            className="text-white py-2 px-4 rounded-md text-sm flex items-center gap-2 justify-center flex-nowrap"
            onClick={handleSync}
          >
            <FaSync className="w-3 h-3" />
            <div className="text-nowrap">Sync Membership</div>
          </button>

          <button
            style={{ backgroundColor: currentColor }}
            className="text-white py-2 px-4 rounded-md text-sm flex items-center gap-2 justify-center flex-nowrap"
            onClick={handleAdd}
          >
            <FaPlus className="w-3 h-3" />
            <div className="text-nowrap">Add Membership</div>
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="w-full cursor-pointer">
          <GridComponent
            dataSource={membershipList}
            allowPaging
            allowSorting
            allowTextWrap={true}
            pageSettings={{
              pageCount: 5,
              pageSizes: ["All", "10", "25", "50"],
            }}
            textWrapSettings={{ wrapMode: "Content" }}
            toolbar={["Search", "ExcelExport"]}
            queryCellInfo={queryCellInfo}
            allowExcelExport={true}
            toolbarClick={toolbarClick}
            dataBound={dataBound}
            ref={gridRef}
          >
            <ColumnsDirective>
              <ColumnDirective
                field="No"
                headerText="No"
                textAlign="Center"
                width="50"
                type="Number"
              />
              <ColumnDirective
                field="Uuid"
                headerText="Action"
                textAlign="Center"
                isPrimaryKey={true}
                minWidth="250"
                template={actionTemplate}
              />
              <ColumnDirective
                field="IntegratedPlatformName"
                headerText="Platform"
                minWidth="150"
              />
              <ColumnDirective
                field="Name"
                headerText="Name"
                width="auto"
                minWidth="150"
              />
              <ColumnDirective
                field="TitleProperty"
                headerText="Title Property"
                minWidth="150"
              />
              <ColumnDirective
                field="KeyProperty"
                headerText="Key Property"
                minWidth="150"
              />
              <ColumnDirective
                field="IsActive"
                headerText="Is Active"
                textAlign="Center"
                width={120}
                template={(props) => (
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      props.IsActive
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {props.IsActive ? "Active" : "Inactive"}
                  </span>
                )}
              />
              <ColumnDirective
                field="CreatedAt"
                headerText="Created At"
                minWidth="200"
                width="100%"
              />
            </ColumnsDirective>
            <Inject
              services={[Search, Toolbar, Page, Sort, Resize, ExcelExport]}
            />
          </GridComponent>
        </div>
      </div>

      {/* Add Modal */}
      {toggleAdd && (
        <div className="overflow-auto absolute z-[99] top-0 left-0 flex justify-center w-full min-h-screen h-full overflow-y-auto py-40 px-[100px] bg-slate-800/50">
          <div className="bg-white flex flex-col gap-2 w-full max-w-[464px] h-fit rounded-xl p-8">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-medium text-nowrap">
                Add Membership
              </h1>
              <div
                className="cursor-pointer p-2"
                onClick={() => {
                  setToggleAdd(false);
                  resetForm();
                }}
              >
                <FaXmark className="w-4 h-4" />
              </div>
            </div>
            <hr />

            {renderFormFields()}

            <div className="flex justify-end items-center gap-2 mt-6">
              <button
                className="text-gray-600 w-24 py-2 px-4 rounded-md text-sm border border-gray-300"
                onClick={() => {
                  setToggleAdd(false);
                  resetForm();
                }}
              >
                Cancel
              </button>
              <button
                disabled={membershipDisabled || loading}
                style={{ backgroundColor: currentColor }}
                className={
                  "text-white py-2 px-4 rounded-md text-sm " +
                  (membershipDisabled || loading
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer")
                }
                onClick={() => handleSubmit(false)}
              >
                {!loading ? "Add Membership" : "Loading..."}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {toggleEdit && (
        <div className="overflow-auto absolute z-[99] top-0 left-0 flex justify-center w-full min-h-screen h-full overflow-y-auto py-40 px-[100px] bg-slate-800/50">
          <div className="bg-white flex flex-col gap-2 w-full max-w-[464px] h-fit rounded-xl p-8">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-medium">Edit Membership</h1>
              <div
                className="cursor-pointer p-2"
                onClick={() => {
                  setToggleEdit(false);
                  resetForm();
                }}
              >
                <FaXmark className="w-4 h-4" />
              </div>
            </div>
            <hr />

            {renderFormFields()}

            <div className="flex justify-end items-center gap-2 mt-6">
              <button
                className="text-gray-600 w-24 py-2 px-4 rounded-md text-sm border border-gray-300"
                onClick={() => {
                  setToggleEdit(false);
                  resetForm();
                }}
              >
                Cancel
              </button>
              <button
                disabled={membershipDisabled || loading}
                style={{ backgroundColor: currentColor }}
                className={
                  "text-white py-2 px-4 rounded-md text-sm " +
                  (membershipDisabled || loading
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer")
                }
                onClick={() => handleSubmit(true)}
              >
                {!loading ? "Update Membership" : "Loading..."}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sync Modal */}
      {toggleSync && (
        <div className="overflow-auto absolute z-[99] top-0 left-0 flex justify-center w-full min-h-screen h-full overflow-y-auto py-40 px-[100px] bg-slate-800/50">
          <div className="bg-white flex flex-col gap-2 w-full max-w-[464px] h-fit rounded-xl p-8">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-medium">Sync Membership</h1>
              <div
                className="cursor-pointer p-2"
                onClick={() => {
                  setToggleSync(false);
                  resetSyncForm();
                }}
              >
                <FaXmark className="w-4 h-4" />
              </div>
            </div>
            <hr />

            {renderSyncFormFields()}

            <div className="flex justify-end items-center gap-2 mt-6">
              <button
                className="text-gray-600 w-24 py-2 px-4 rounded-md text-sm border border-gray-300"
                onClick={() => {
                  setToggleSync(false);
                  resetSyncForm();
                }}
              >
                Cancel
              </button>
              <button
                disabled={
                  syncMembershipWarning.integrated_platform_id || loading
                }
                style={{ backgroundColor: currentColor }}
                className={
                  "text-white py-2 px-4 rounded-md text-sm " +
                  (syncMembershipWarning.integrated_platform_id || loading
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer")
                }
                onClick={() => handleSyncSubmit()}
              >
                {!loading ? "Sync Membership" : "Loading..."}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {toggleDetail && (
        <div className="overflow-auto absolute z-[99] top-0 left-0 flex justify-center w-full min-h-screen h-full overflow-y-auto py-40 px-[100px] bg-slate-800/50">
          <div className="bg-white flex flex-col gap-2 w-full max-w-[464px] h-fit rounded-xl p-8">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-medium">Membership Details</h1>
              <div
                className="cursor-pointer p-2"
                onClick={() => {
                  setToggleDetail(false);
                  resetForm();
                }}
              >
                <FaXmark className="w-4 h-4" />
              </div>
            </div>
            <hr />

            {renderFormFields()}

            <div className="flex justify-end items-center mt-6">
              <button
                className="text-gray-600 w-24 py-2 px-4 rounded-md text-sm border border-gray-300"
                onClick={() => {
                  setToggleDetail(false);
                  resetForm();
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {toggleDelete && (
        <div className="overflow-auto absolute z-[99] top-0 left-0 flex justify-center w-full min-h-screen h-full overflow-y-auto py-40 px-[100px] bg-slate-800/50">
          <div className="bg-white flex flex-col gap-4 w-full max-w-[400px] h-fit rounded-xl p-8">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-medium">Delete Membership</h1>
              <div
                className="cursor-pointer p-2"
                onClick={() => {
                  setToggleDelete(false);
                  resetForm();
                }}
              >
                <FaXmark className="w-4 h-4" />
              </div>
            </div>
            <hr />

            <div className="text-center">
              <FaTrash className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-lg font-semibold mb-2">Are you sure?</h2>
              <p className="text-gray-600 mb-4">
                Do you really want to delete the membership "
                {membershipData.name}"? This action cannot be undone.
              </p>
            </div>

            <div className="flex justify-center items-center gap-2 mt-4">
              <button
                className="text-gray-600 w-24 py-2 px-4 rounded-md text-sm border border-gray-300"
                onClick={() => {
                  setToggleDelete(false);
                  resetForm();
                }}
              >
                Cancel
              </button>
              <button
                disabled={deleteLoading}
                className={
                  "bg-red-500 text-white w-24 py-2 px-4 rounded-md text-sm " +
                  (deleteLoading
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer")
                }
                onClick={handleDeleteConfirm}
              >
                {!deleteLoading ? "Delete" : "Deleting..."}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipTypes;
